import React from 'react';
import { Card } from 'react-bootstrap';
import { LuPencil } from 'react-icons/lu';
import { IoTrashOutline } from 'react-icons/io5';
import styled from '@emotion/styled';
import { formatUTCDate } from 'utils/dateTime';
import { Roles } from 'utils/enums';

import { PostedMessage } from './types';

const BURN_CENTER_ROLES: string[] = [Roles.manager, Roles.burnCenterUser];

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  max-width: 100%;
`;

const ListTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a2b3c;
`;

const ListSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const MessageCard = styled(Card)`
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  box-shadow: none;
  background-color: #fff;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;

  .card-body {
    padding: 1.25rem;
    min-width: 0;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const MessageTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1a2b3c;
  line-height: 1.3;
`;

const MessageDate = styled.span`
  font-size: 0.875rem;
  color: #9ca3af;
  line-height: 1.3;
`;

const DeleteIconButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: #9ca3af;
  font-size: 1.125rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: #6b7280;
  }

  &:focus-visible {
    outline: 2px solid #008995;
    outline-offset: 2px;
    border-radius: 0.25rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const MessageBody = styled.div`
  font-size: 0.9375rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1rem;
  min-width: 0;
  max-width: 100%;

  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;

  p {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  img {
    display: block;
    max-width: 100%;
    width: auto !important;
    height: auto !important;
    margin: 0.5rem 0;
    object-fit: contain;
  }

  a {
    color: #008995;
    overflow-wrap: break-word;
  }

  ul,
  ol {
    margin: 0 0 0.5rem;
    padding-left: 1.5rem;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const RoleTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  border-radius: 9999px;
  background-color: #fff;
  border: 1px solid #d1d5db;
  color: #1a2b3c;
`;

const BurnCenterTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  border-radius: 9999px;
  background-color: #1a2b3c;
  color: #fff;
`;

const EmptyState = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

type Props = {
  messages: PostedMessage[];
  historyMonths?: number;
  editingMessageId?: string | null;
  onEdit: (message: PostedMessage) => void;
  onDelete: (id: string) => void;
};

const cleanHtml = (html: string) => html
  .replace(/&nbsp;/g, ' ')
  .replace(/\u00A0/g, ' ');

const AudienceTags: React.FC<{ message: PostedMessage }> = ({ message }) => {
  if (message.isAllUsers) {
    return (
      <TagRow>
        <RoleTag>All users</RoleTag>
      </TagRow>
    );
  }

  const targetsBurnCenterRoles = message.isAllRoles
    || (message.roles ?? []).some((role) => BURN_CENTER_ROLES.includes(role));

  return (
    <TagRow>
      {message.isAllRoles ? (
        <RoleTag>All roles</RoleTag>
      ) : (
        (message.roleLabels ?? []).map((label) => (
          <RoleTag key={label}>{label}</RoleTag>
        ))
      )}
      {targetsBurnCenterRoles && (
        message.isAllBurnCenters ? (
          <BurnCenterTag>All burn centers</BurnCenterTag>
        ) : (
          <>
            {(message.burnCenterLabels ?? []).map((label) => (
              <BurnCenterTag key={label}>{label}</BurnCenterTag>
            ))}
            {(message.accessLevels ?? []).map((level) => (
              <BurnCenterTag key={`access-${level}`}>{level}</BurnCenterTag>
            ))}
          </>
        )
      )}
    </TagRow>
  );
};

const PostedMessagesList: React.FC<Props> = ({
  messages,
  historyMonths,
  editingMessageId,
  onEdit,
  onDelete,
}) => (
  <ListWrapper>
    <div>
      <ListTitle>Posted messages</ListTitle>
      {historyMonths != null && (
        <ListSubtitle>
          {`Messages from the last ${historyMonths} month${historyMonths === 1 ? '' : 's'}`}
        </ListSubtitle>
      )}
    </div>

    {messages.length === 0 ? (
      <EmptyState>No messages posted in this period.</EmptyState>
    ) : (
      messages.map((message) => (
        <MessageCard key={message.id} className={editingMessageId === message.id ? 'border-secondary' : undefined}>
          <Card.Body>
            <MessageHeader>
              <TitleBlock>
                <MessageTitle>{message.title}</MessageTitle>
                <MessageDate>
                  {formatUTCDate(message.createdAt, 'MMMM D, YYYY')}
                </MessageDate>
              </TitleBlock>
              <ActionButtons>
                <DeleteIconButton
                  type="button"
                  onClick={() => onEdit(message)}
                  aria-label={`Edit message ${message.title}`}
                >
                  <LuPencil />
                </DeleteIconButton>
                <DeleteIconButton
                  type="button"
                  onClick={() => onDelete(message.id)}
                  aria-label={`Delete message ${message.title}`}
                >
                  <IoTrashOutline />
                </DeleteIconButton>
              </ActionButtons>
            </MessageHeader>

            <MessageBody
              dangerouslySetInnerHTML={{
                __html: cleanHtml(message.body),
              }}
            />

            <AudienceTags message={message} />
          </Card.Body>
        </MessageCard>
      ))
    )}
  </ListWrapper>
);

export default PostedMessagesList;
